import { APP_CONFIG } from '@/config/app';
import { User } from '@/types/auth';

/** Granted by Dash, per account: may open the dashboard and correct attendance. */
export const CLIENT_INTERNAL_SUPER_ADMIN_ROLE = 'client_internal_super_admin';
/** The client's own super admin: may open the dashboard. The API confines
 *  what they see to their assigned pitstops — all of them when none are. */
export const CLIENT_SUPER_ADMIN_ROLE = 'client_super_admin';
/** Sees only the pitstops assigned to them. */
export const CLIENT_SUPERVISOR_ROLE = 'client_supervisor';

// Two lists on purpose, even while they match: widening who counts as a
// super admin on the attendance list must not widen who opens the dashboard.
const DASHBOARD_ROLES = [
  CLIENT_INTERNAL_SUPER_ADMIN_ROLE,
  CLIENT_SUPER_ADMIN_ROLE,
];

const SUPER_ADMIN_ROLES = [
  CLIENT_INTERNAL_SUPER_ADMIN_ROLE,
  CLIENT_SUPER_ADMIN_ROLE,
];

function hasAnyRole(user: User | null, roleNames: string[]): boolean {
  return (user?.roles ?? []).some((role) => roleNames.includes(role?.name));
}

/**
 * The dashboard is switched on per client through
 * REACT_APP_ENABLE_DASHBOARD_PROVIDER_IDS. A user whose provider is not
 * listed never sees it, whatever role they hold; a profile without a
 * providerId (the JWT-only fallback in AuthContext) reads as not listed.
 */
function isDashboardEnabledForProvider(user: User | null): boolean {
  const providerId = user?.providerId;
  if (providerId == null) return false;
  return APP_CONFIG.enableDashboardProviderIds.includes(providerId);
}

/**
 * Kept in one place so the sidebar and the route guard can never drift
 * apart. Both gates must pass: the right role, and a provider the dashboard
 * is switched on for. The API enforces the role; the provider list is a
 * front-end rollout switch. Roles are matched literally — client_admin /
 * client_staff are not implied.
 */
export function canAccessShiftDashboard(user: User | null): boolean {
  return (
    hasAnyRole(user, DASHBOARD_ROLES) && isDashboardEnabledForProvider(user)
  );
}

/** Correcting a record stays with Dash's own people, on any provider. */
export function canEditAttendance(user: User | null): boolean {
  return hasAnyRole(user, [CLIENT_INTERNAL_SUPER_ADMIN_ROLE]);
}

/**
 * Either super admin, Dash's or the client's. Checks every role the user
 * holds: /users/me returns roles in no fixed order, so the first one is not
 * a reliable answer.
 */
export function isSuperAdmin(user: User | null): boolean {
  return hasAnyRole(user, SUPER_ADMIN_ROLES);
}

export function isSupervisor(user: User | null): boolean {
  return hasAnyRole(user, [CLIENT_SUPERVISOR_ROLE]);
}
